<template>
	<div class="resultados_tab report_theme_container" data-theme="resultados">
		<TabHeader
			title="Estado de resultados"
			description="Visualiza el estado de tu negocio en un solo lugar."
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			:compact="true"
			calendar-mode="range"
			:date-range="calendarRange"
			:year-range="yearRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange" />

		<!-- Table Section -->
		<div class="underHeader">
			<FinancialResultsTable
				class="mt-4"
				:rows="tableRows"
				:headers="getHeadersTable"
				:isLoading="isLoading"
				:filterPeriod="filterPeriod"
				:expandedRows="expandedRows"
				:show-period-select="false"
				:cell-tooltips="true"
				navigable
				@toggle-childs="changeViewTotalBills"
				@navigate-row="onNavigateRow"
				@navigate-cell="onNavigateCell" />
		</div>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { defaultYearRange } from "@/store/financialReports/utils/dateRange";

import TabHeader from "@/components/financialReports/TabHeader.vue";
import FinancialResultsTable from "@/components/financialReports/FinancialResultsTable.vue";
import { TAB_IDS } from "@/views/financialReports/config/tabs";

// Mapa fila del P&L → tab destino. Click en una fila salta a su tab; las filas
// de Ventas/Costos además abren el modal del mes (esos tabs tienen drill).
// EBITDA y Utilidad Antes de Impuestos son montos calculados que Rentabilidad
// descompone en sus escalones de margen → navegan allá (sin drill).
const ROW_TO_TAB = {
	ventas_netas: TAB_IDS.VENTAS,
	costos_productos: TAB_IDS.COSTOS,
	costos_variables: TAB_IDS.COSTOS,
	costos_directos: TAB_IDS.COSTOS,
	costos_fijos: TAB_IDS.COSTOS,
	margen_producto: TAB_IDS.RENTABILIDAD,
	margen_bruto: TAB_IDS.RENTABILIDAD,
	margen_contribucion: TAB_IDS.RENTABILIDAD,
	EBITDA: TAB_IDS.RENTABILIDAD,
	"margen_EBITDA_%": TAB_IDS.RENTABILIDAD,
	utilidad_antes_de_impuestos: TAB_IDS.RENTABILIDAD,
	"margen_neto_%": TAB_IDS.RENTABILIDAD,
	gastos_totales: TAB_IDS.GASTOS,
	gastos_administracion: TAB_IDS.GASTOS,
	gastos_comerciales: TAB_IDS.GASTOS,
	"gastos_i+d": TAB_IDS.GASTOS,
	"gastos/ventas_%": TAB_IDS.GASTOS,
	no_operacional: TAB_IDS.NO_OPER,
	ingresos_no_operacional: TAB_IDS.NO_OPER,
	gastos_no_operacional: TAB_IDS.NO_OPER,
};

// Tabs que tienen un modal de drill por celda (abren el análisis del mes). Para
// el resto el click en celda sólo navega al tab.
const TABS_WITH_DRILL = [TAB_IDS.VENTAS, TAB_IDS.COSTOS, TAB_IDS.GASTOS];

export default {
	name: "ResultadosTab",
	components: {
		TabHeader,
		FinancialResultsTable
	},
	data() {
		return {
			totalBillsExpanded: false,
			filterOptions: FILTER_PERIOD_OPTIONS
		};
	},
	computed: {
		...mapState("reportsFilters", ["endMonth", "selectedPeriodRange", "customRange", "customYearRange"]),
		...mapState("financialReports", [
			"filterPeriod",
			"tableRowHeaders",
			"netSellsDetail",
			"maxCostsDetail",
			"maxExpensesDetail",
			"isLoading"
		]),
		...mapGetters("financialReports", {
			getHeadersTableFunc: "getHeadersTable"
		}),
		getHeadersTable() {
			return this.getHeadersTableFunc();
		},
		tableRows() {
			const headers = this.tableRowHeaders;
			return Object.entries(headers)
				.filter(([, row]) => row.isShowed !== false)
				// `targetTab` habilita la afordancia de navegación por fila en la
				// tabla (null = no navegable, p.ej. EBITDA / Utilidad).
				.map(([key, row]) => ({ ...row, id: key, targetTab: ROW_TO_TAB[key] || null }));
		},
		expandedRows() {
			return this.totalBillsExpanded ? ["gastos_totales"] : [];
		},
		getNow() {
			return moment().toDate();
		},
		// Rango activo del calendar del header (modo range, igual que Ventas). Si
		// el store tiene `customRange` lo usamos tal cual; si no, derivamos un
		// default (últimos 12 meses sobre endMonth) para que el picker abra
		// pintando "Último año" en vez de "—". Es sólo visual: el fetch sigue
		// usando selectedPeriodRange hasta que el usuario elija un rango.
		calendarRange() {
			if (this.customRange) return this.customRange;
			const end = moment(this.endMonth).startOf("month");
			return { from: end.clone().subtract(11, "months").toDate(), to: end.toDate() };
		},
		// Rango de años del picker de Anual: el custom elegido, o el default de
		// Anual (mismo `defaultYearRange` que usa el fetch) para que el trigger
		// abra pintando el span real y nunca difiera de la tabla.
		yearRange() {
			return this.customYearRange || defaultYearRange(this.endMonth);
		},
		// Firma reactiva consolidada — un solo trigger para fetch aunque
		// varios filtros cambien en la misma transacción. Incluye customRange
		// y customYearRange para refetchear cuando el usuario mueve el rango.
		fetchSignature() {
			return [
				this.filterPeriod,
				this.endMonth,
				this.selectedPeriodRange,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},
	},
	watch: {
		fetchSignature() {
			this.fetchReportData();
		},
	},
	mounted() {
		this.fetchReportData();
	},
	methods: {
		...mapActions("financialReports", ["fetchReportData"]),
		...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH", "SET_PENDING_DRILL"]),
		...mapMutations("financialReports", [
			"SET_FILTER_PERIOD",
			"TOGGLE_CHILD_ROWS"
		]),
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		// El calendar del header (modo range) emite el rango elegido; la action
		// sincroniza customRange + selectedPeriodRange="custom" + endMonth.
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		// El picker de Anual emite el rango de años; estado ortogonal al mes
		// (no toca endMonth/selectedPeriodRange).
		updateYearRange(range) {
			this.setCustomYearRange(range);
		},
		// Único toggle expandible en Resultados; el evento del componente trae
		// rowId pero solo gastos_totales lo emite porque es la única fila con
		// hasChilds. Si en el futuro se agregan más, expandir a un Set por id.
		changeViewTotalBills(rowId) {
			if (rowId !== "gastos_totales") return;
			this.totalBillsExpanded = !this.totalBillsExpanded;
			this.TOGGLE_CHILD_ROWS(this.totalBillsExpanded);
		},
		// Cambia el tab activo vía query param (la URL es la fuente de verdad del
		// tab activo; DashboardReports lee $route.query.tab). No-op si ya estamos
		// en ese tab.
		goToTab(tab) {
			if (this.$route.query.tab === tab) return;
			this.$router
				.replace({ query: { ...this.$route.query, tab } })
				.catch((err) => { if (err.name !== "NavigationDuplicated") throw err; });
		},
		// Click en el nombre de una fila → sólo salta al tab de esa fila.
		onNavigateRow({ rowId }) {
			const tab = ROW_TO_TAB[rowId];
			if (tab) this.goToTab(tab);
		},
		// Click en una celda de valor → salta al tab y, si ese tab tiene modal de
		// drill (Ventas/Costos) y estamos en granularidad mensual, deja un
		// pendingDrill { tab, monthLabel } que el tab destino consume al montar
		// para abrir el modal de ese mes. En trimestral/anual la celda agrupa
		// varios meses, así que sólo navega.
		onNavigateCell({ rowId, monthLabel }) {
			const tab = ROW_TO_TAB[rowId];
			if (!tab) return;
			if (this.filterPeriod === "month" && TABS_WITH_DRILL.includes(tab)) {
				this.SET_PENDING_DRILL({ tab, monthLabel });
			}
			this.goToTab(tab);
		},
	},
};
</script>

<style scoped lang="scss">
.resultados_tab {
	position: relative;
	// Respiro inferior para que la card no quede pegada al borde del viewport
	// (el sistema pide "denso pero respirable", no apretado).
	padding-bottom: 24px;
	height: 100%;
}

// Header común vive en TabHeader.vue.

.has-gap-cards {
	gap: 16px;
}
</style>
